// src/pages/SettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next'; // Импорт перевода
import { getUserProfile, changePassword, ApiError } from '../api/apiClient';

const SettingsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(); // Инициализация хука
  
  // --- STATE ---
  const [profileData, setProfileData] = useState({ email: '', login: '' });
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  
  // Состояние формы пароля
  const [passForm, setPassForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passLoading, setPassLoading] = useState(false);
  
  // Сообщения
  const [statusMsg, setStatusMsg] = useState({ type: '', text: '' });

  // --- EFFECTS ---
  useEffect(() => {
    if (user) {
      setProfileData(prev => ({ ...prev, login: user.login || '' }));
      fetchProfile();
    } else {
      setIsLoadingProfile(false);
    }
  }, [user]);

  // --- API CALLS ---
  const fetchProfile = async () => {
    try {
      const data = await getUserProfile();
      setProfileData({
        login: data.login || data.username || user?.login || 'N/A',
        email: data.email || data.userProfile?.email || 'Email not found', 
      });
    } catch (error) {
      console.error("Failed to fetch profile", error);
      if (user) {
         setStatusMsg({ type: 'error', text: t('settings.msg_error_load') });
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setStatusMsg({ type: '', text: '' });

    if (passForm.newPassword !== passForm.confirmPassword) {
      setStatusMsg({ type: 'error', text: t('settings.msg_error_match') });
      return;
    }

    if (passForm.newPassword.length < 6) {
        setStatusMsg({ type: 'error', text: t('settings.msg_error_length') });
        return;
    }

    setPassLoading(true);
    try {
      await changePassword(passForm.currentPassword, passForm.newPassword);
      setStatusMsg({ type: 'success', text: t('settings.msg_success') });
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const errorText = error instanceof ApiError 
        ? (error.data?.message || error.message) 
        : t('settings.msg_error_change');
      setStatusMsg({ type: 'error', text: errorText });
    } finally {
      setPassLoading(false);
    }
  };

  const handlePassInput = (e) => {
    const { name, value } = e.target;
    setPassForm(prev => ({ ...prev, [name]: value }));
  };

  // --- ICONS ---
  const IconUser = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const IconShield = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );

  const IconLock = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  const IconAlert = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const IconCheck = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-10">
      
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">{t('settings.title')}</h1>
        <p className="text-slate-400 mt-2">{t('settings.desc')}</p>
      </div>

      {/* Status Message Banner */}
      {statusMsg.text && (
        <div className={`p-4 rounded-lg flex items-center gap-3 ${
          statusMsg.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
        }`}>
          {statusMsg.type === 'success' ? <IconCheck /> : <IconAlert />}
          <span>{statusMsg.text}</span>
        </div>
      )}

      {/* Section 1: Profile Information */}
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700 flex items-center gap-3">
          <div className="p-2 bg-blue-500/20 text-blue-400 rounded-lg">
            <IconUser />
          </div>
          <h2 className="text-xl font-semibold text-white">{t('settings.profile_title')}</h2>
        </div>
        
        <div className="p-8 grid gap-6 md:grid-cols-2">
          {/* Login Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-400">{t('settings.login_label')}</label>
            <div className="relative">
                <input 
                    type="text" 
                    value={isLoadingProfile ? t('settings.loading') : profileData.login} 
                    disabled
                    className="w-full bg-slate-900/50 border border-slate-700 text-slate-300 rounded-lg px-4 py-3 focus:outline-none cursor-not-allowed opacity-70"
                />
            </div>
            <p className="text-xs text-slate-500">{t('settings.login_hint')}</p>
          </div>

          {/* Email Field */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-400">{t('settings.email_label')}</label>
            <div className="relative">
                <input 
                    type="email" 
                    value={isLoadingProfile ? t('settings.loading') : profileData.email} 
                    disabled 
                    className="w-full bg-slate-900/50 border border-slate-700 text-slate-300 rounded-lg px-4 py-3 focus:outline-none cursor-not-allowed opacity-70"
                />
            </div>
            <p className="text-xs text-slate-500">{t('settings.email_hint')}</p>
          </div>
        </div>
      </div>

      {/* Section 2: Security (Password) */}
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700 flex items-center gap-3">
             <div className="p-2 bg-purple-500/20 text-purple-400 rounded-lg">
                <IconShield />
             </div>
          <h2 className="text-xl font-semibold text-white">{t('settings.security_title')}</h2>
        </div>

        <form onSubmit={handlePasswordChange} className="p-8 space-y-6">
          
          <div className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">{t('settings.current_pass')}</label>
                <div className="relative">
                    <input 
                        type="password" 
                        name="currentPassword"
                        value={passForm.currentPassword}
                        onChange={handlePassInput}
                        placeholder={t('settings.current_pass_placeholder')}
                        className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all"
                        required
                    />
                    <div className="absolute right-4 top-3.5 text-slate-500">
                        <IconLock />
                    </div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 pt-2">
                {/* New Password */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">{t('settings.new_pass')}</label>
                    <input 
                        type="password" 
                        name="newPassword"
                        value={passForm.newPassword}
                        onChange={handlePassInput}
                        placeholder={t('settings.new_pass_placeholder')}
                        className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all"
                        required
                    />
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">{t('settings.confirm_pass')}</label>
                    <input 
                        type="password" 
                        name="confirmPassword"
                        value={passForm.confirmPassword}
                        onChange={handlePassInput}
                        placeholder={t('settings.confirm_pass_placeholder')}
                        className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-4 py-3 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-all"
                        required
                    />
                </div>
            </div>
          </div>

          <div className="pt-4 flex justify-end border-t border-slate-700/50">
             <button 
                type="submit" 
                disabled={passLoading}
                className={`px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg shadow-lg shadow-blue-900/20 transition-all flex items-center gap-2 ${passLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
             >
                {passLoading ? (
                    <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                        {t('settings.btn_saving')}
                    </>
                ) : (
                    t('settings.btn_save')
                )}
             </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsPage;